"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect } from "react";
import { Menu, X, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Drawer, DrawerClose, DrawerContent, DrawerFooter, DrawerHeader, DrawerTitle, DrawerTrigger } from "@/components/ui/drawer";
import { cn } from "@/lib/utils";
function DesktopMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="16078" className="flex gap-8">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-6 w-20 bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="font-sans font-semibold text-foreground relative group py-1 tracking-tight">
                {item.label}
                <span className="absolute left-0 bottom-0 w-full h-[2px] bg-destructive scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 p-4">
        {[1, 2, 3, 4].map(i => <div key={i} className="h-8 w-full bg-muted animate-pulse rounded-md" />)}
      </div>;
  }
  return <nav className="flex flex-col gap-6 p-4">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-xl font-sans font-bold text-foreground hover:text-primary transition-colors tracking-tight">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="humberg-electrical-services" className={cn("sticky top-0 z-50 w-full transition-all duration-300 border-b border-transparent", isScrolled ? "bg-background/80 backdrop-blur-xl border-border shadow-md" : "bg-background")}>
      <div className="container mx-auto px-4 md:px-8 lg:px-16 h-20 flex items-center justify-between">
        {}
        <div className="flex-shrink-0">
          <Link to="/HomePage">
            <img src="https://humorous-shiner.10web.cloud/wp-content/uploads/2026/03/humberg-logo.jpg" alt="Humberg Electrical Services" className="h-8 object-contain !w-auto" />
          </Link>
        </div>

        {}
        <div className="hidden lg:flex items-center gap-8">
          <MenuProvider menu_id="42" className="flex items-center">
            <DesktopMenuConsumer />
          </MenuProvider>
        </div>

        {}
        <div className="hidden lg:block">
          <Button asChild className="rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 px-8 font-sans font-bold uppercase tracking-wider animate-pulse hover:animate-none transition-all">
            <Link to="/ContactPage">Request Service</Link>
          </Button>
        </div>

        {}
        <div className="lg:hidden">
          <SafeHydrate><Drawer open={isMobileOpen} onOpenChange={setIsMobileOpen} direction="right">
            <DrawerTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DrawerTrigger>
            <DrawerContent className="h-full w-[300px] ml-auto rounded-l-none rounded-r-none bg-background border-l border-border">
              <DrawerHeader className="flex items-center justify-between border-b border-border p-4">
                <DrawerTitle>
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1603327/img/maintenapro_logo.png" alt="Humberg Electrical Services" className="h-6 w-auto" />
                </DrawerTitle>
                <DrawerClose asChild>
                  <Button variant="ghost" size="icon">
                    <X className="h-5 w-5" />
                    <span className="sr-only">Close menu</span>
                  </Button>
                </DrawerClose>
              </DrawerHeader>

              <div className="flex-1 overflow-y-auto py-4">
                <MenuProvider menu_id="42">
                  <MobileMenuConsumer />
                </MenuProvider>
              </div>

              <DrawerFooter className="border-t border-border p-4">
                <Button asChild className="w-full rounded-sm bg-destructive text-destructive-foreground hover:bg-destructive/90 font-sans font-bold h-12 text-lg uppercase tracking-wider">
                  <Link to="/ContactPage">Request Service</Link>
                </Button>
              </DrawerFooter>
            </DrawerContent>
          </Drawer></SafeHydrate>
        </div>
      </div>
    </header>;
}